import { EMPTY_OBJECT } from '../../../constants/common'
import { getGooglePlaceGeocode } from '../../Google'
import {
  InputSelectControl,
  InputSelectControlIndicatorsSymbolCounter,
  Select,
  SelectDropdownAsyncList,
  useSelectStates,
} from '../../Select'
import { toAddressSelectItemWithCoordsFromGeocode } from '../Address.utils'
import { AddressSelectDropdownListFooter } from '../AddressSelectDropdownListFooter'

import { ADDRESS_SELECT_MAX_LENGTH } from './AddressSelect.constants'
import { useAddressSelectGoogleMapsApiPlacesAutocomplete } from './AddressSelect.hooks'
import {
  focusAddressSelectInputAfterOpen,
  showAddressSelectDropdownListFooter,
} from './AddressSelect.utils'

function AddressSelect({
  controlProps,
  disabled,
  dropdownFloatingConfig,
  dropdownProps,
  error,
  listProps: {
    Footer: listFooter = AddressSelectDropdownListFooter,
    ...listProps
  } = EMPTY_OBJECT,
  maxLength = ADDRESS_SELECT_MAX_LENGTH,
  value,
  onChangeSearch,
  onClear,
  onCloseDropdown,
  onSelectAll,
  onSelectItem,
  onShowMap,
  ...restProps
}) {
  const {
    fetchPredictions,
    handleChangeSelectSearch,
    handleClearSelectSearch,
    handleResetSession,
    isLoading,
    searchValue,
    suggestions: options,
  } = useAddressSelectGoogleMapsApiPlacesAutocomplete({
    maxLength,
    value,
    onChangeSearch,
  })

  function handleOpenDropdown() {
    if (searchValue) {
      fetchPredictions(searchValue)
    }
  }

  const {
    activeIndex,
    context,
    elementsRef,
    floatingStyles,
    getFloatingItemProps,
    getFloatingProps,
    getReferenceProps,
    handleClose,
    handleKeyDown,
    handleOpen,
    handleSelect,
    handleToggleOpen,
    isOpen,
    refs,
  } = useSelectStates({
    clearSearchOnSelect: false,
    disabled,
    dropdownFloatingConfig,
    options,
    onClose: onCloseDropdown,
    onOpen: handleOpenDropdown,
    onSelectItem,
  })

  function handleClearSearch() {
    handleClearSelectSearch()
    onClear()
  }

  function handleSelectItem(item) {
    getGooglePlaceGeocode({ address: item.label }).then(response => {
      const nextValue = toAddressSelectItemWithCoordsFromGeocode(
        item,
        response.data.results[0]
      )

      handleSelect(nextValue)
      handleResetSession()
    })
  }

  return (
    <Select
      {...restProps}
      Control={InputSelectControl}
      List={SelectDropdownAsyncList}
      activeIndex={activeIndex}
      contextProps={{
        disabled,
        error,
        getFloatingItemProps,
        searchValue,
        value,
        onChangeSearch: handleChangeSelectSearch,
        onClearSearch: handleClearSearch, // this select has no option to clear value without clearing search value
        onClose: handleClose,
        onOpen: handleOpen,
        onSelect: handleSelectItem,
        onSelectAll,
        onToggleOpen: handleToggleOpen,
      }}
      controlProps={{
        ...controlProps,
        ...getReferenceProps({ disabled, onKeyDown: handleKeyDown }),
        contentProps: {
          ...controlProps?.contentProps,
          ValueItem: null,
        },
        indicatorsProps: {
          ...controlProps?.indicatorsProps,
          SymbolCounter:
            controlProps?.indicatorsProps?.SymbolCounter ??
            InputSelectControlIndicatorsSymbolCounter,
          symbolCounterProps: {
            ...controlProps?.indicatorsProps?.symbolCounterProps,
            maxLength,
          },
        },
        onFocusAfterOpen: focusAddressSelectInputAfterOpen,
      }}
      dropdownProps={{
        ...dropdownProps,
        context,
        floatingStyles,
        getFloatingProps,
      }}
      isOpen={isOpen}
      listProps={{
        ...listProps,
        Footer: listFooter,
        elementsRef,
        footerProps: {
          onShowMap,
        },
        headerProps: {
          Search: null,
        },
        loadingInitial: isLoading,
        moreItemsLoaderProps: {
          ...listProps.moreItemsLoaderProps,
          hasText: true,
        },
        showFooter: showAddressSelectDropdownListFooter,
      }}
      options={options}
      refs={refs}
    />
  )
}

export default AddressSelect
